// src/components/admin/page-management/section-forms/StatisticsForm.js
import React from 'react';
import { Plus, Trash, MoveUp, MoveDown } from 'lucide-react';
import ColorPicker from '../../shared/ColorPicker';

const StatisticsForm = ({ content, updateContent }) => {
  // Handle adding a new statistic
  const handleAddStatistic = () => {
    const statistics = [...(content?.statistics || [])];
    statistics.push({
      value: "100+",
      label: "New Statistic",
      delay: statistics.length * 200
    });
    updateContent('statistics', statistics);
  };

  // Handle removing a statistic
  const handleRemoveStatistic = (index) => {
    const statistics = [...(content?.statistics || [])];
    statistics.splice(index, 1);
    updateContent('statistics', statistics);
  };

  // Handle moving a statistic up
  const handleMoveUp = (index) => {
    if (index === 0) return;
    const statistics = [...(content?.statistics || [])];
    const temp = statistics[index];
    statistics[index] = statistics[index - 1];
    statistics[index - 1] = temp;
    updateContent('statistics', statistics);
  };

  // Handle moving a statistic down
  const handleMoveDown = (index) => {
    const statistics = [...(content?.statistics || [])];
    if (index === statistics.length - 1) return;
    const temp = statistics[index];
    statistics[index] = statistics[index + 1];
    statistics[index + 1] = temp;
    updateContent('statistics', statistics);
  };

  // Handle updating a statistic
  const handleUpdateStatistic = (index, field, value) => {
    const statistics = [...(content?.statistics || [])];
    statistics[index] = {
      ...statistics[index],
      [field]: value
    };
    updateContent('statistics', statistics);
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Statistics Section</h3>
      
      {/* Statistics Items */}
      <div className="bg-gray-50 p-4 rounded-md">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium">Statistics</h4>
          <button
            type="button"
            onClick={handleAddStatistic}
            className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center"
          >
            <Plus size={16} className="mr-1" /> Add Statistic
          </button>
        </div>
        
        {(content?.statistics || []).map((stat, index) => (
          <div 
            key={index} 
            className="mb-6 p-4 border border-gray-200 rounded bg-white"
          >
            <div className="flex justify-between items-center mb-2">
              <h5 className="font-medium">Statistic #{index + 1}</h5>
              <div className="flex space-x-1">
                <button
                  type="button"
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0}
                  className={`p-1 rounded ${index === 0 ? 'text-gray-300' : 'text-gray-600 hover:bg-gray-100'}`}
                  title="Move Up"
                >
                  <MoveUp size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => handleMoveDown(index)}
                  disabled={index === (content?.statistics || []).length - 1}
                  className={`p-1 rounded ${index === (content?.statistics || []).length - 1 ? 'text-gray-300' : 'text-gray-600 hover:bg-gray-100'}`}
                  title="Move Down"
                >
                  <MoveDown size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => handleRemoveStatistic(index)}
                  className="p-1 text-red-500 hover:bg-red-50 rounded"
                  title="Remove"
                >
                  <Trash size={16} />
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Value</label>
                <input
                  type="text"
                  value={stat.value}
                  onChange={(e) => handleUpdateStatistic(index, 'value', e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="e.g., 1000+, 98%, 24/7"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Label</label>
                <input
                  type="text"
                  value={stat.label}
                  onChange={(e) => handleUpdateStatistic(index, 'label', e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="e.g., Happy Clients"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Animation Delay (ms)</label>
              <input
                type="number"
                min="0"
                step="100"
                value={stat.delay}
                onChange={(e) => handleUpdateStatistic(index, 'delay', parseInt(e.target.value, 10))}
                className="w-32 p-2 border rounded"
              />
              <p className="text-xs text-gray-500 mt-1">
                Only applies when animations are enabled.
              </p>
            </div>
          </div>
        ))}
        
        {(content?.statistics || []).length === 0 && (
          <p className="text-center py-4 text-gray-500">
            No statistics added. Click the button above to add one.
          </p>
        )}
      </div>
      
      {/* Layout Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Layout Settings</h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Container Max Width</label>
          <select
            value={content?.containerMaxWidth || 'max-w-7xl'}
            onChange={(e) => updateContent('containerMaxWidth', e.target.value)}
            className="w-full p-2 border rounded"
          >
            <option value="max-w-5xl">Narrow (max-w-5xl)</option>
            <option value="max-w-6xl">Standard (max-w-6xl)</option>
            <option value="max-w-7xl">Wide (max-w-7xl)</option>
            <option value="">Full Width</option>
          </select>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Mobile Columns</label>
            <select
              value={content?.statsPerRow?.mobile || 1}
              onChange={(e) => updateContent('statsPerRow', {
                ...content?.statsPerRow,
                mobile: parseInt(e.target.value)
              })}
              className="w-full p-2 border rounded"
            >
              <option value={1}>1 Column</option>
              <option value={2}>2 Columns</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tablet Columns</label>
            <select
              value={content?.statsPerRow?.tablet || 2}
              onChange={(e) => updateContent('statsPerRow', {
                ...content?.statsPerRow,
                tablet: parseInt(e.target.value)
              })}
              className="w-full p-2 border rounded"
            >
              <option value={1}>1 Column</option>
              <option value={2}>2 Columns</option>
              <option value={3}>3 Columns</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Desktop Columns</label>
            <select
              value={content?.statsPerRow?.desktop || 4}
              onChange={(e) => updateContent('statsPerRow', {
                ...content?.statsPerRow,
                desktop: parseInt(e.target.value)
              })}
              className="w-full p-2 border rounded"
            >
              <option value={2}>2 Columns</option>
              <option value={3}>3 Columns</option>
              <option value={4}>4 Columns</option>
              <option value={5}>5 Columns</option>
            </select>
          </div>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Card Gap</label>
          <input
            type="text"
            value={content?.cardGap || '2rem'}
            onChange={(e) => updateContent('cardGap', e.target.value)}
            className="w-full p-2 border rounded"
            placeholder="e.g., 2rem, 32px"
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Padding Top</label>
            <input
              type="text"
              value={content?.paddingTop || '8rem'}
              onChange={(e) => updateContent('paddingTop', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="e.g., 8rem, 128px"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Padding Bottom</label>
            <input
              type="text"
              value={content?.paddingBottom || '10rem'}
              onChange={(e) => updateContent('paddingBottom', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="e.g., 10rem, 160px"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Margin Bottom</label>
            <input
              type="text"
              value={content?.marginBottom || '5rem'}
              onChange={(e) => updateContent('marginBottom', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="e.g., 5rem, 80px"
            />
          </div>
        </div>
      </div>
      
      {/* Background Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Background Settings</h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Background Color</label>
          <ColorPicker
            color={content?.backgroundColor || '#033568'}
            onChange={(color) => updateContent('backgroundColor', color)}
          />
        </div>
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.backgroundOverlayEnabled !== false}
              onChange={(e) => updateContent('backgroundOverlayEnabled', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Enable Gradient Overlay</span>
          </label>
        </div>
        
        {content?.backgroundOverlayEnabled !== false && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1">Gradient Start Color</label>
              <ColorPicker
                color={content?.overlayFrom || 'rgba(31, 41, 55, 0.3)'}
                onChange={(color) => updateContent('overlayFrom', color)}
                allowAlpha={true}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Gradient End Color</label>
              <ColorPicker
                color={content?.overlayTo || 'rgba(30, 58, 138, 0.3)'}
                onChange={(color) => updateContent('overlayTo', color)}
                allowAlpha={true}
              />
            </div>
          </div>
        )}
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.showFloatingOrbs !== false}
              onChange={(e) => updateContent('showFloatingOrbs', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Show Floating Orbs</span>
          </label>
        </div>
        
        {content?.showFloatingOrbs !== false && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1">Orb 1 Color</label>
              <ColorPicker
                color={content?.orbColors?.[0] || '#FFEB3B'}
                onChange={(color) => updateContent('orbColors', [
                  color,
                  content?.orbColors?.[1] || '#4299E1'
                ])}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Orb 2 Color</label>
              <ColorPicker
                color={content?.orbColors?.[1] || '#4299E1'}
                onChange={(color) => updateContent('orbColors', [
                  content?.orbColors?.[0] || '#FFEB3B',
                  color
                ])}
              />
            </div>
          </div>
        )}
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.showSeparatorLines !== false}
              onChange={(e) => updateContent('showSeparatorLines', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Show Separator Lines</span>
          </label>
        </div>
        
        {content?.showSeparatorLines !== false && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1">Top Separator Color</label>
              <ColorPicker
                color={content?.separatorColorTop || 'rgba(1,50,99,0.8)'}
                onChange={(color) => updateContent('separatorColorTop', color)}
                allowAlpha={true}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Bottom Separator Color</label>
              <ColorPicker
                color={content?.separatorColorBottom || 'rgba(1,50,99,0.8)'}
                onChange={(color) => updateContent('separatorColorBottom', color)}
                allowAlpha={true}
              />
            </div>
          </div>
        )}
      </div>
      
      {/* Card Appearance */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Card Appearance</h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Card Background Color</label>
          <ColorPicker
            color={content?.cardBackgroundColor || 'rgba(255, 255, 255, 0.05)'}
            onChange={(color) => updateContent('cardBackgroundColor', color)}
            allowAlpha={true}
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Card Border Color</label>
          <ColorPicker
            color={content?.cardBorderColor || 'rgba(255, 255, 255, 0.1)'}
            onChange={(color) => updateContent('cardBorderColor', color)}
            allowAlpha={true}
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Border Radius</label>
          <input
            type="text"
            value={content?.borderRadius || '0.5rem'}
            onChange={(e) => updateContent('borderRadius', e.target.value)}
            className="w-full p-2 border rounded"
            placeholder="e.g., 0.5rem, 8px"
          />
        </div>
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.shadowEnabled !== false}
              onChange={(e) => updateContent('shadowEnabled', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Show Card Shadow</span>
          </label>
        </div>
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.cardHoverEffect !== false}
              onChange={(e) => updateContent('cardHoverEffect', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Enable Card Hover Effect</span>
          </label>
        </div>
      </div>
      
      {/* Text Appearance */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Text Appearance</h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Value Text Color</label>
          <ColorPicker
            color={content?.textColor || '#ffffff'}
            onChange={(color) => updateContent('textColor', color)}
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Label Text Color</label>
          <ColorPicker
            color={content?.statLabelColor || '#d1d5db'}
            onChange={(color) => updateContent('statLabelColor', color)}
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Value Text Size</label>
          <select
            value={content?.statValueSize || 'text-4xl md:text-5xl'}
            onChange={(e) => updateContent('statValueSize', e.target.value)}
            className="w-full p-2 border rounded"
          >
            <option value="text-3xl md:text-4xl">Small</option>
            <option value="text-4xl md:text-5xl">Medium</option>
            <option value="text-5xl md:text-6xl">Large</option>
          </select>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Label Text Size</label>
          <select
            value={content?.statLabelSize || 'text-lg'}
            onChange={(e) => updateContent('statLabelSize', e.target.value)}
            className="w-full p-2 border rounded"
          >
            <option value="text-base">Small</option>
            <option value="text-lg">Medium</option>
            <option value="text-xl">Large</option>
          </select>
        </div>
      </div>
      
      {/* Animation Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Animation Settings</h4>
        
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.animationEnabled !== false}
              onChange={(e) => updateContent('animationEnabled', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Enable Animations</span>
          </label>
        </div>
        
        {content?.animationEnabled !== false && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Animation Style</label>
            <select
              value={content?.animationStyle || 'fade'}
              onChange={(e) => updateContent('animationStyle', e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="fade">Fade</option>
              <option value="slide">Slide</option>
              <option value="scale">Scale</option>
            </select>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatisticsForm;